export const state = () => ({
  subscription: null,
  isLoaded: false,
  companyId: null,
  requestId: 0,
});

const PLAN_TYPE_BY_CODE = {
  FREE: 0,
  BASIC: 1,
  PREMIUM: 2,
  ENTERPRISE: 3,
  TRIAL: 4,
};

const PREMIUM_ONLY_FEATURES = [
  'document_uploads',
  'subcontractors',
  'dashboard',
  'reports',
  'schedule_filters',
  'time_filters',
  'advanced_export',
  'settings_modification',
];

function normalizeSubscription(subscription) {
  if (!subscription) {
    return null;
  }

  const planCode = subscription.plan_code || '';
  const hasPremiumFeatures = ['PREMIUM', 'ENTERPRISE', 'TRIAL'].includes(planCode);
  const normalizedFeatures = { ...(subscription.features || {}) };

  PREMIUM_ONLY_FEATURES.forEach((feature) => {
    normalizedFeatures[feature] = hasPremiumFeatures && subscription.is_active === true && subscription.features?.[feature] === true;
  });

  return {
    ...subscription,
    type: PLAN_TYPE_BY_CODE[planCode] ?? null,
    features: normalizedFeatures,
  };
}

export const mutations = {
  BEGIN_SUBSCRIPTION_FETCH(state, companyId) {
    state.requestId += 1;
    if (Number(state.companyId) !== Number(companyId)) {
      state.subscription = null;
      state.isLoaded = false;
    }
    state.companyId = companyId;
  },
  SET_SUBSCRIPTION(state, subscription) {
    state.subscription = subscription;
    state.isLoaded = true;
  },
  CLEAR_SUBSCRIPTION(state) {
    state.subscription = null;
    state.isLoaded = false;
    state.companyId = null;
    state.requestId += 1;
  }
};

export const getters = {
  // Plan information
  isFreePlan: (state) => state.subscription?.plan_code === 'FREE',
  isPaid: (state) => ['BASIC', 'PREMIUM', 'ENTERPRISE'].includes(state.subscription?.plan_code),
  isTrial: (state) => state.subscription?.trial?.is_trial === true,

  // Limits
  canAddProfile: (state) => state.subscription?.limits?.profiles?.can_add ?? false,
  canAddClient: (state) => state.subscription?.limits?.clients?.can_add ?? false,
  profileLimit: (state) => state.subscription?.limits?.profiles ?? null,
  clientLimit: (state) => state.subscription?.limits?.clients ?? null,

  // Feature permissions
  hasFeature: (state) => (feature) => {
    return state.subscription?.features?.[feature] ?? false;
  },

  // Trial information
  trialDaysRemaining: (state) => state.subscription?.trial?.days_remaining ?? 0,
  trialEndsAt: (state) => state.subscription?.trial?.ends_at ?? null,
  isTrialExpired: (state) => state.subscription?.trial?.is_expired === true,

  // Plan details
  planName: (state) => state.subscription?.plan ?? '',
  planCode: (state) => state.subscription?.plan_code ?? '',
  isActive: (state) => state.subscription?.is_active ?? false,
  requiresActivation: (state) => state.subscription?.requires_activation ?? false
};

export const actions = {
  async fetchSubscription({ commit, rootState, state }, companyIdOverride = null) {
    try {
      const activeProfileCompanyId = rootState?.user?.activeProfile?.company_id;
      const authCompanyId = this.$auth?.user?.companyId || this.$auth?.user?.company_id;
      const companyId = companyIdOverride || activeProfileCompanyId || authCompanyId;
      commit('BEGIN_SUBSCRIPTION_FETCH', companyId);
      const requestId = state?.requestId;
      const apiClient = this.$api || this.$axios;

      const response = await apiClient.get('/subscription', {
        params: companyId ? { companyId } : {}
      });
      const subscription = normalizeSubscription(response.data.data);
      const currentCompanyId = rootState?.user?.activeProfile?.company_id;
      if ((state && requestId !== state.requestId)
        || (!companyIdOverride && currentCompanyId && Number(currentCompanyId) !== Number(companyId))) {
        return null;
      }

      commit('SET_SUBSCRIPTION', subscription);

      if (this.$auth?.user?.uid && typeof this.$auth.setUser === 'function') {
        this.$auth.setUser({
          ...this.$auth.user,
          subscription,
        });
      }

      return subscription;
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      // Avoid forcing FREE on transient API issues; keep previous state.
      return null;
    }
  },

  async startTrial({ dispatch }) {
    try {
      const apiClient = this.$api || this.$axios;
      await apiClient.post('/subscription/start-trial');
      await dispatch('fetchSubscription');
      return true;
    } catch (error) {
      console.error('Failed to start trial:', error);
      return false;
    }
  },

  async activateSubscription({ dispatch }, token) {
    try {
      const apiClient = this.$api || this.$axios;
      await apiClient.post('/subscription/activate', { token });
      await dispatch('fetchSubscription');
      return true;
    } catch (error) {
      console.error('Failed to activate subscription:', error);
      return false;
    }
  },

  clearSubscription({ commit }) {
    commit('CLEAR_SUBSCRIPTION');
  }
};
